import { openChangePaymentModal, openCreditCardModal } from '~/actions/billing.actions';
import { ActionKeys } from '~/actions/constants';
import { ActionTypes } from '~/actions/index';
import { mergeDeepRight } from 'ramda';
import { Asset, AssetType } from '~/types/Asset';
import { Brand } from '~/types/Brand';
import Company from '~/types/Company';
import User from '~/types/User';
import {
  getFailed,
  getNotRequested,
  getRefreshFailed,
  getRefreshing,
  getRefreshSuccess,
  getRequesting,
  getSuccess,
} from '~/utils/fetchable';

export interface Activation {
  ordersCount?: number | null;
}

export type NextPaymentModalAction =
  | ReturnType<typeof openCreditCardModal>
  | ReturnType<typeof openChangePaymentModal>;

export type UserDraftRequests = {
  [T in AssetType]: Map<number, Requestable<null, string>>;
};

export type UserState = {
  readonly isLoading: boolean;
  readonly isAuthenticated: boolean;
  readonly user: Readonly<Maybe<User>>;
  readonly company: Readonly<Maybe<Company>>;
  readonly isLoadingCompany: boolean;
  readonly isUpdatingCreditCard: boolean;
  readonly isRefreshingUser: boolean;
  readonly assets: Refreshable<Asset[], string>;
  readonly assetDraft: Requestable<null, string>;
  readonly assetDrafts: UserDraftRequests;
  readonly brand: Readonly<Maybe<Brand>>;
  readonly activation: Activation;
  readonly showPromoApplicationNotice: boolean;
  readonly userErrorMessage: string;
  readonly showEmailVerificationModal: boolean;
  readonly nextModalAction: Readonly<Maybe<NextPaymentModalAction>>;
  readonly companyUpdateRequest: Requestable<null, string>;
};

const initializeUserAssetDrafts = (numAssets = 1): Map<number, Requestable<null, string>> => {
  let assetMap = new Map<number, Requestable<null, string>>();
  for (let i = 0; i < numAssets; i++) {
    assetMap.set(i + 1, getNotRequested());
  }
  return assetMap;
};

export const initialState: UserState = {
  isLoading: true,
  isAuthenticated: false,
  user: null,
  company: null,
  isLoadingCompany: false,
  isUpdatingCreditCard: false,
  isRefreshingUser: false,
  assets: getNotRequested(),
  assetDraft: getNotRequested(),
  assetDrafts: {
    [AssetType.LOGO]: initializeUserAssetDrafts(),
    [AssetType.PROMO]: initializeUserAssetDrafts(2),
    [AssetType.LOGO_BW]: initializeUserAssetDrafts(),
    [AssetType.RECOMMENDATION]: initializeUserAssetDrafts(4),
    [AssetType.PROMO_DELIVERED]: initializeUserAssetDrafts(),
    [AssetType.SPOTLIGHT_DELIVERED]: initializeUserAssetDrafts(),
    [AssetType.SPOTLIGHT_VIDEO_DELIVERED]: initializeUserAssetDrafts(),
    [AssetType.RECOMMENDATION_DELIVERED]: initializeUserAssetDrafts(4),
  },
  brand: null,
  activation: {
    ordersCount: null,
  },
  showPromoApplicationNotice: false,
  userErrorMessage: '',
  showEmailVerificationModal: false,
  nextModalAction: null,
  companyUpdateRequest: getNotRequested(),
};

function userReducer(state: UserState = initialState, action: ActionTypes): UserState {
  let assetDraftsByType;
  switch (action.type) {
    case ActionKeys.UPDATE_PACKAGE_DEFAULT_UNITS_SUCCESS:
      return mergeDeepRight(state, {
        user: {
          defaultDistanceUnit: action.payload.defaultDistanceUnit,
          defaultMassUnit: action.payload.defaultWeightUnit,
        },
      }) as UserState;
    case ActionKeys.USER_SET_UNAUTHENTICATED:
      return {
        ...state,
        isAuthenticated: false,
      };
    case ActionKeys.USER_FETCH:
      return {
        ...state,
        isLoading: true,
      };
    case ActionKeys.COMPANY_FETCH:
      return {
        ...state,
        isLoadingCompany: true,
      };
    case ActionKeys.COMPANY_FETCH_SUCCESS:
    case ActionKeys.COMPANY_UPDATE_SUCCESS:
      return {
        ...state,
        company: action.payload,
        isLoadingCompany: false,
      };
    case ActionKeys.COMPANY_FETCH_ERROR:
      return {
        ...state,
        isLoadingCompany: false,
      };
    case ActionKeys.USER_FETCH_SUCCESS:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: true,
        isRefreshingUser: false,
        user: action.payload.user,
      };
    case ActionKeys.USER_FETCH_ERROR:
      return {
        ...state,
        isLoading: false,
        isAuthenticated: false,
        isRefreshingUser: false,
      };
    case ActionKeys.UPDATE_USER_DEFAULT_SERVICE_LEVEL:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              defaultProvider: action.defaultProvider,
              defaultServiceLevel: action.defaultServiceLevel,
            }
          : null,
      };
    case ActionKeys.UPDATE_USER_DEFAULT_RETURN_SERVICE_LEVEL:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              defaultReturnProvider: action.defaultReturnProvider,
              defaultReturnServiceLevel: action.defaultReturnServiceLevel,
            }
          : null,
      };
    case ActionKeys.UPDATE_DEFAULT_ADDRESS:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              address: action.payload.address,
            }
          : null,
      };
    case ActionKeys.UPDATE_DEFAULT_RETURN_ADDRESS:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              addressReturn: action.payload.address,
            }
          : null,
      };
    case ActionKeys.USER_UPDATE_CREDIT_CARD:
      return {
        ...state,
        isUpdatingCreditCard: true,
      };
    case ActionKeys.USER_UPDATE_CREDIT_CARD_ERROR:
    case ActionKeys.USER_UPDATE_CREDIT_CARD_SUCCESS:
      return {
        ...state,
        isUpdatingCreditCard: false,
        user: state.user
          ? {
              ...state.user,
            }
          : null,
      };
    case ActionKeys.USER_REFRESH:
      return {
        ...state,
        isRefreshingUser: true,
      };
    case ActionKeys.USER_ASSETS_FETCH:
      return {
        ...state,
        assets: getRefreshing(state.assets),
      };
    case ActionKeys.USER_ASSETS_FETCH_SUCCESS:
      return {
        ...state,
        assets: getRefreshSuccess(state.assets, action.payload.assets),
      };
    case ActionKeys.USER_ASSETS_FETCH_ERROR:
      return {
        ...state,
        assets: getRefreshFailed(state.assets, action.payload.errorCode),
      };

    case ActionKeys.USER_ASSET_UPLOAD_DRAFT:
    case ActionKeys.USER_ASSET_CLEAR_DRAFT:
    case ActionKeys.USER_ASSET_REVERT_DRAFT:
    case ActionKeys.USER_ASSET_PUBLISH:
      assetDraftsByType = state.assetDrafts[action.payload.assetType];
      assetDraftsByType.set(action.payload.rank, getRequesting());
      return {
        ...state,
        assetDraft: getRequesting(),
        assetDrafts: {
          ...state.assetDrafts,
          [action.payload.assetType]: assetDraftsByType,
        },
      };
    case ActionKeys.USER_FIRST_LABEL_PURCHASED:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
              hasPurchasedLabel: true,
            }
          : null,
      };
    case ActionKeys.ORDERS_BATCH_CREATE_TRANSACTIONS_SUCCESS:
      return {
        ...state,
        user: state.user
          ? {
              ...state.user,
            }
          : null,
      };
    case ActionKeys.USER_ASSET_UPLOAD_DRAFT_SUCCESS:
    case ActionKeys.USER_ASSET_CLEAR_DRAFT_SUCCESS:
    case ActionKeys.USER_ASSET_REVERT_DRAFT_SUCCESS:
    case ActionKeys.USER_ASSET_PUBLISH_SUCCESS:
      assetDraftsByType = state.assetDrafts[action.payload.assetType];
      assetDraftsByType.set(action.payload.rank, getSuccess(null));
      return {
        ...state,
        assetDraft: getSuccess(null),
        assetDrafts: {
          ...state.assetDrafts,
          [action.payload.assetType]: assetDraftsByType,
        },
      };
    case ActionKeys.USER_ASSET_UPLOAD_DRAFT_ERROR:
    case ActionKeys.USER_ASSET_CLEAR_DRAFT_ERROR:
    case ActionKeys.USER_ASSET_REVERT_DRAFT_ERROR:
    case ActionKeys.USER_ASSET_PUBLISH_ERROR:
      assetDraftsByType = state.assetDrafts[action.payload.assetType];
      assetDraftsByType.set(action.payload.rank, getFailed(action.payload.errorCode));
      return {
        ...state,
        assetDraft: getFailed(action.payload.errorCode),
        assetDrafts: {
          ...state.assetDrafts,
          [action.payload.assetType]: assetDraftsByType,
        },
      };
    case ActionKeys.UPDATE_ACTIVATION_DATA:
      return {
        ...state,
        activation: {
          ...state.activation,
          ...action.payload,
        },
      };
    case ActionKeys.SHOW_PROMO_APPLICATION_NOTICE:
      return {
        ...state,
        showPromoApplicationNotice: true,
      };
    case ActionKeys.DISMISS_PROMO_APPLICATION_NOTICE:
      return {
        ...state,
        showPromoApplicationNotice: false,
      };
    case ActionKeys.USER_UPDATE_SUCCESS:
      return mergeDeepRight(state, {
        user: {
          ...state.user,
          ...action.payload.user,
        },
      }) as UserState;
    case ActionKeys.USER_UPDATE_ERROR:
      return {
        ...state,
        userErrorMessage: action.payload.error,
      };
    case ActionKeys.SHOW_EMAIL_VERIFICATION_MODAL:
      return {
        ...state,
        nextModalAction: action.payload.nextModalAction,
        showEmailVerificationModal: true,
      };
    case ActionKeys.CLOSE_EMAIL_VERIFICATION_MODAL:
      return {
        ...state,
        nextModalAction: null,
        showEmailVerificationModal: false,
      };
    default:
      return state;
  }
}

export default userReducer;
